import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClock,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactScreen = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL || "",
      });

      await axiosInstance.post("/api/contacts", formData);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err: any) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-gray-100 py-16 sm:py-24 relative overflow-hidden">
      {/* Dynamic Background Gradients */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-900/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-24">
          <span className="text-primary-500 font-semibold tracking-wider text-sm uppercase">
            Get In Touch
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-white bg-gradient-to-r from-white via-neutral-200 to-neutral-400 bg-clip-text text-transparent">
            We'd Love to Hear From You
          </h1>
          <p className="mt-4 text-neutral-400 text-lg sm:text-xl">
            Have questions about our car wash services, packages, or bookings? 
            Send us a message and our professional team will get back to you shortly.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Info Side (5 Columns) */}
          <div className="lg:col-span-5 space-y-8">
            <h2 className="text-2xl font-semibold text-white">Contact Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {/* Phone Card */}
              <a
                href="tel:+971569949911"
                className="group flex gap-4 p-5 rounded-2xl bg-neutral-900/40 backdrop-blur-md border border-neutral-800 hover:border-primary-500/50 hover:bg-neutral-900/60 transition-all duration-300 shadow-lg"
              >
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary-950/60 border border-primary-500/30 text-primary-500 group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={faPhone} className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Call Us</h3>
                  <p className="mt-1 text-sm text-neutral-400">Available 8 AM - 6 PM</p>
                  <p className="mt-2 font-medium text-primary-400 group-hover:text-primary-300 transition-colors">
                    +971 56 994 9911
                  </p>
                </div>
              </a>

              {/* Email Card */}
              <a
                href="mailto:orders@carveel.com"
                className="group flex gap-4 p-5 rounded-2xl bg-neutral-900/40 backdrop-blur-md border border-neutral-800 hover:border-primary-500/50 hover:bg-neutral-900/60 transition-all duration-300 shadow-lg"
              >
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary-950/60 border border-primary-500/30 text-primary-500 group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={faEnvelope} className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Email Us</h3>
                  <p className="mt-1 text-sm text-neutral-400">Response within 24 hours</p>
                  <p className="mt-2 font-medium text-primary-400 group-hover:text-primary-300 transition-colors break-all">
                    orders@carveel.com
                  </p>
                </div>
              </a>

              {/* Address Card */}
              <div className="group flex gap-4 p-5 rounded-2xl bg-neutral-900/40 backdrop-blur-md border border-neutral-800 shadow-lg">
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary-950/60 border border-primary-500/30 text-primary-500">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Headquarters</h3>
                  <p className="mt-1 text-sm text-neutral-400">Mobile Services Available across</p>
                  <p className="mt-2 font-medium text-neutral-300">
                    Dubai, United Arab Emirates
                  </p>
                </div>
              </div>

              {/* Hours Card */}
              <div className="group flex gap-4 p-5 rounded-2xl bg-neutral-900/40 backdrop-blur-md border border-neutral-800 shadow-lg">
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary-950/60 border border-primary-500/30 text-primary-500">
                  <FontAwesomeIcon icon={faClock} className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Working Hours</h3>
                  <p className="mt-1 text-sm text-neutral-400">Open Daily</p>
                  <p className="mt-2 font-medium text-neutral-300">
                    Monday - Sunday: 8:00 AM - 10:00 PM
                  </p>
                </div>
              </div>
            </div>

            {/* Interactive Map Embed */}
            <div className="rounded-2xl overflow-hidden border border-neutral-800 shadow-2xl h-64 sm:h-72">
              <iframe
                title="Carveel Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3609.680655380582!2d55.2721877!3d25.2048493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43348a6d4957%3A0x7841614728562a0a!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                className="w-full h-full border-0 filter invert opacity-80 contrast-125 brightness-90 hover:opacity-100 transition-opacity duration-300"
                allowFullScreen={false}
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* Form Side (7 Columns) */}
          <div className="lg:col-span-7 p-6 sm:p-10 rounded-3xl bg-neutral-900/30 backdrop-blur-md border border-neutral-800 shadow-2xl">
            <h2 className="text-2xl font-semibold text-white mb-6">Send Us a Message</h2>

            {/* Notification Banners */}
            {success && (
              <div className="mb-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-start gap-3 animate-fade-in">
                <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Message Sent Successfully!</h4>
                  <p className="mt-1 text-sm text-emerald-400/90">
                    Thank you for reaching out. An agent from our customer support team will contact you shortly.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-8 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 flex items-start gap-3 animate-fade-in">
                <FontAwesomeIcon icon={faExclamationCircle} className="w-5 h-5 text-rose-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Failed to Submit</h4>
                  <p className="mt-1 text-sm text-rose-400/90">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-300 mb-2">
                    Full Name <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 bg-neutral-950/60 border border-neutral-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-white rounded-xl placeholder-neutral-600 transition-all outline-none"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-2">
                    Email Address <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="johndoe@example.com"
                    className="w-full px-4 py-3 bg-neutral-950/60 border border-neutral-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-white rounded-xl placeholder-neutral-600 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Phone Number */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-neutral-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+971 56 123 4567"
                    className="w-full px-4 py-3 bg-neutral-950/60 border border-neutral-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-white rounded-xl placeholder-neutral-600 transition-all outline-none"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-neutral-300 mb-2">
                    Subject <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className="w-full px-4 py-3 bg-neutral-950/60 border border-neutral-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-white rounded-xl placeholder-neutral-600 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-neutral-300 mb-2">
                  Message <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Type your message here..."
                  className="w-full px-4 py-3 bg-neutral-950/60 border border-neutral-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-white rounded-xl placeholder-neutral-600 transition-all outline-none resize-none"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 disabled:opacity-75 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-neutral-900 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-primary-900/20 cursor-pointer"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting Message...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactScreen;
